/******************************************************************************
 *
 * Copyright (c) 2019-2020 ServiceActivator Inc. All Rights Reserved.
 *
 * Unauthorized copying of the code in this file, completely or partially,
 * via any medium is strictly prohibited.
 *
 * Proprietary and Confidential.
 *
 * Written by 'Vishal Goel’ <vishal@wakencode.com>, February 2020
 *
 *****************************************************************************/

import React, { useState, useRef, useCallback, useEffect } from "react";
import {
  LoadScript,
  GoogleMap,
  Polygon,
  DrawingManager,
  Marker,
} from "@react-google-maps/api";
import SideDrawer from "../SideDrawer/SideDrawer";
import Greeting from "../../../Greeting/Greeting";
import { useSelector, useDispatch, shallowEqual } from "react-redux";
import {
  saveGeofenceGeometries,
  updatePropertyStoreData,
} from "./../../../store/actions";
import uuid from "uniqid";
import cloneDeep from "lodash/cloneDeep";
import findIndex from "lodash/findIndex";
import {
  prepareGeofencePostData,
  isPathEqual,
  preparePropertiesForStoreUpdate,
} from "./../../../shared/geofence";
import TopButton from "./../TopButton/TopButton";
import { toast } from "react-toastify";
import ConfirmDialog from "./../ConfirmDialog/ConfirmDialog";
import find from "lodash/find";
import { constant } from "./../../../shared/constants/constants";
import { scroller } from "react-scroll";
import GeometryInfo from "./GeometryInfo";
import WorkOrderInfo from "./WorkOrderInfo";
import DeleteConfirmDialog from "./../DeleteConfirmDialog/DeleteConfirmDialog";
import styles from "./Geo.module.css";
import AllPolygons from "./AllPolygons";
import  LoadingWithoutAuth  from "../../../../src/Loading/LoadingWithoutAuth";

const Geo = () => {
  const dispatch = useDispatch();

  const geofenceData = useSelector(
    (state) => state.geofence.geofenceData,
    shallowEqual
  );
  const profileName = useSelector((state) => state.auth.profileName);
  // const isVisible = useSelector(
  //   (state) => state.geoUtil.showUserInfoBox,
  //   shallowEqual
  // );

  useEffect(() => {
    // show by default hybrid view if propertyId is present in query params
    let params = new URLSearchParams(window.location.search);
    const mode = params.get("mode");
    let propertyId = params.get("propertyId");
    if (propertyId) mapView.current = "hybrid";
    const isCreateProperty = mode === "createProperty";
    if (isCreateProperty) {setSideDrawerVisibility(false)
      setisSideDrawerVisible(true);
    };
  }, []);

  /**
   * check if geometries are present in default selected property
   * if not present hide side drawer and enable edit mode
   * otherwise do nothing
   */
  useEffect(() => {
    if (geofenceData.length === 1) {
      let defaultSelectedProperty = find(geofenceData, (g) => g.isSelected);
      if (defaultSelectedProperty) {
        if (defaultSelectedProperty.geometries.length < 1) {
          toast.info(constant.GEOFENCE_NOT_EXIST);
          setSideDrawerVisibility(false);
        }
      }
    }
  }, [geofenceData]);

  const nextSelectedProperty = useRef();
  const mapView = useRef("roadmap");
  const mapRef = useRef(null);
  const drawingRef = useRef(null);
  const isGeometryEdited = useRef(false);
  const [isSideDrawerVisible, setSideDrawerVisibility] = useState(true);
  const [selectedPropertyInfo, setSelectedPropertyInfo] = useState({});
  const [selectedPropertyGeometry, setSelectedPropertyGeometry] = useState([]);
  const [selectedGeometry, setSelectedGeometry] = useState(null);
  const [isDialogVisible, setDialogVisible] = useState(false);
  const [isCreateProperty, setisSideDrawerVisible] = useState(false);
  const [isDeleteDialogVisible, setDeleteDialogVisible] = useState(false);

  // Define refs for Polygon instance and listeners
  const polygonRef = useRef([]);
  const listenersRef = useRef([]);

  // Call setPath with new edited path
  const onEdit = useCallback(
    (gid, index) => {
      if (polygonRef.current) {
        const nextPath = polygonRef.current[index]
          .getPath()
          .getArray()
          .map((latLng) => {
            return { lat: latLng.lat(), lng: latLng.lng() };
          });
        // check if edited path or previous path is equal or not
        // if not equal set newly edited geometry
        let isEqual = isPathEqual(
          nextPath,
          selectedPropertyGeometry[index].path
        );
        isGeometryEdited.current = !isEqual || isGeometryEdited.current;
        if (!isEqual) updateGeometry(gid, nextPath);
      }
    },
    [selectedPropertyGeometry]
  );

  // Bind refs to current Polygon and listeners
  const onLoad = useCallback(
    (polygon, gid) => {
      polygonRef.current.push(polygon);
      const path = polygon.getPath();
      let index = polygonRef.current.length - 1;
      listenersRef.current.push(
        path.addListener("set_at", () => onEdit(gid, index)),
        path.addListener("insert_at", () => onEdit(gid, index)),
        path.addListener("remove_at", () => onEdit(gid, index))
      );
    },
    [onEdit]
  );

  // Clean up refs
  const onUnmount = useCallback((gid, index) => {
    // listenersRef.current.forEach((lis) => lis.remove());
    polygonRef.current.splice(index, 1);
  }, []);

  // called when click on property card to select the card
  const propertySelectionHandler = (info) => {
    // store info next property going to select
    nextSelectedProperty.current = info;

    // if same property is selected ignore
    if (
      nextSelectedProperty.current.propertyId ===
      selectedPropertyInfo.propertyId
    )
      return;
    // if geometry is edited show dialog box before selecting the other property
    if (isGeometryEdited.current) setDialogVisible(true);
    // if geometry is not edited
    else propertySelectHelper();
  };

  // property select helper method
  const propertySelectHelper = () => {
    polygonRef.current = [];
    // scroll up the selected property
    scroller.scrollTo(nextSelectedProperty.current.propertyId, {
      duration: 800,
      delay: 0,
      smooth: "easeInOutQuart",
      containerId: "scroll-container",
      offset: -110,
    });
    isGeometryEdited.current = false;

    // set the info of selected property in state
    setSelectedPropertyInfo({
      propertyId: nextSelectedProperty.current.propertyId,
      button: "",
      center: nextSelectedProperty.current.center,
      zoom: nextSelectedProperty.current.zoom,
    });

    // set selected property geometries in state
    setSelectedPropertyGeometry(
      cloneDeep(nextSelectedProperty.current.geometries)
    );
    geometrySelectionHandler();
  };

  // select property by clicking geometry on map
  const propertySelectedByClickingGeometry = (propertyId) => () => {
    let property = find(geofenceData, (g) => g.propertyId === propertyId);
    propertySelectionHandler(property);
  };

  // helper method to store newly created geometry/polygon path in state
  const createNewGeometry = (path) => {
    isGeometryEdited.current = true;
    let geometry = {
      geometryId: null,
      gid: uuid(),
      path: path,
    };
    setSelectedPropertyGeometry((geometries) => {
      let updatedGeometries = [...geometries];
      updatedGeometries.push(geometry);
      return updatedGeometries;
    });
    // select newly created geometry
    setSelectedGeometry(geometry);
  };

  // method to delete geometry
  const deleteGeometryWrapper = () => {
    if (!selectedGeometry) {
      toast.warn(constant.SELECT_ANY_GEOMETRY_TO_DELETE);
      return;
    }
    // show confirmation dialog box
    setDeleteDialogVisible(true);
  };

  // helper method to delete geometry from state
  const deleteGeometry = () => {
    isGeometryEdited.current = true;
    setSelectedPropertyGeometry((geometries) => {
      let updatedGeometries = [...geometries];
      updatedGeometries = updatedGeometries.filter(
        (geo) => geo.gid !== selectedGeometry.gid
      );

      // save updated geometries
      saveGeometriesHandler(null, updatedGeometries);
      return updatedGeometries;
    });
  };

  // update the edited geometry in state
  const updateGeometry = (gid, path) => {
    isGeometryEdited.current = true;
    setSelectedPropertyGeometry((geometries) => {
      let updatedGeometries = [...geometries];
      let index = findIndex(updatedGeometries, (o) => o.gid === gid);
      updatedGeometries[index].path = path;
      return updatedGeometries;
    });
  };

  // select geometry by clicking it on map
  const geometrySelectionHandler = (event, geometry) => {
    if (geometry) setSelectedGeometry(geometry);
    else setSelectedGeometry(null);
  };

  // called when polygon is completed
  const onPolygonComplete = (polygon) => {
    let path = polygon.getPath();
    let coordinates = [];

    for (let i = 0; i < path.length; i++) {
      coordinates.push({
        lat: path.getAt(i).lat(),
        lng: path.getAt(i).lng(),
      });
    }

    createNewGeometry(coordinates);

    // delete the newly created geometry from map
    // as we are storing it in state
    polygon.setMap(null);
  };

  // save geometries handler (call api to save geometries)
  const saveGeometriesHandler = (event, editedGeometry) => {
    // if side drawer is hidden then show it
    if (!isSideDrawerVisible 
      && !isCreateProperty
      ) setSideDrawerVisibility(true);

    // prepare data for post request
    let postData = prepareGeofencePostData(
      editedGeometry ? editedGeometry : selectedPropertyGeometry
    );

    // save geometries on server
    dispatch(saveGeofenceGeometries(selectedPropertyInfo.propertyId, postData));

    // prepare data for store update
    let updatedData = preparePropertiesForStoreUpdate(
      geofenceData,
      editedGeometry ? editedGeometry : selectedPropertyGeometry,
      selectedPropertyInfo.propertyId
    );
    // update edited geometry in store
    if (isGeometryEdited.current)
      dispatch(updatePropertyStoreData(updatedData));
    isGeometryEdited.current = false;
  };

  // toggle visibility of side drawer
  const toggleSideDrawer = () => {
    setSideDrawerVisibility((isSideDrawerVisible) => !isSideDrawerVisible);
  };

  // handle for which property edit mode is enabled
  const editButtonHandler = (id) => {
    setSelectedPropertyInfo((selectedPropertyInfo) => {
      const updatedInfo = { ...selectedPropertyInfo };
      updatedInfo.button = id;
      return updatedInfo;
    });
  };

  // called when map id changes
  const onMapTypeIdChanged = () => {
    if (mapRef.current) {
      mapView.current = mapRef.current.state.map.mapTypeId;
    }
  };

  let centerLat = 43.167125915000305;
  let centerLng = -101.79176330566406;
  if (selectedPropertyInfo.center) {
    centerLat = parseFloat(selectedPropertyInfo.center.lat);
    centerLng = parseFloat(selectedPropertyInfo.center.lng);
  }

  //  hide drawing mode is user is not admin
  let showDrawingTools = true;
  if (profileName && !profileName.toLowerCase().includes("admin"))
    showDrawingTools = false;

  return (
    <div className="App">
      {!selectedPropertyInfo  || Object.keys(selectedPropertyInfo).length === 0 && (
        <LoadingWithoutAuth/> // Show shimmer effect when selectedPropertyInfo is null
      )}      
        <LoadScript
          id="script-loader"
          googleMapsApiKey="AIzaSyBnvRm7g7qHl47C3slFh-YYr8onuu84Ih4"
          language="en"
          libraries={["drawing", "geometry", "places"]}
          region="us">
          <GoogleMap
            ref={mapRef}
            mapContainerClassName="App-map"
            onMapTypeIdChanged={onMapTypeIdChanged}
            mapContainerStyle={{
              width: isSideDrawerVisible ? "calc(100% - 370px)" : "100%",
            }}
            center={{ lat: centerLat, lng: centerLng }}
            onClick={geometrySelectionHandler}
            options={{
              fullscreenControl: false,
              mapTypeId: mapView.current,
              streetViewControl: false,
            }}
            zoom={selectedPropertyInfo.zoom ? selectedPropertyInfo.zoom : 5}
            version="weekly"
            tilt={0}>
            {(selectedPropertyInfo.button || !isSideDrawerVisible) &&
              showDrawingTools && (
                <DrawingManager
                  ref={drawingRef}
                  options={{
                    drawingControl:
                      selectedPropertyInfo.button || !isSideDrawerVisible,
                    drawingControlOptions: {
                      position: 2,
                      drawingModes: ["polygon"],
                    },
                  }}
                  onPolygonComplete={onPolygonComplete}
                />
              )}
            <AllPolygons
              selectProperty={propertySelectedByClickingGeometry}
              selectedPropertyId={selectedPropertyInfo.propertyId}
            />
            {selectedPropertyGeometry.map((poly, ind) => (
              <Polygon
                key={poly.gid}
                onClick={(e) => geometrySelectionHandler(e, poly)}
                editable={
                  (!!selectedPropertyInfo.button || !isSideDrawerVisible) &&
                  showDrawingTools
                }
                draggable={
                  (!!selectedPropertyInfo.button || !isSideDrawerVisible) &&
                  showDrawingTools
                }
                path={poly.path}
                onMouseUp={() => onEdit(poly.gid, ind)}
                onDragEnd={() => onEdit(poly.gid, ind)}
                onLoad={(polygon) => onLoad(polygon, poly.gid)}
                onUnmount={() => onUnmount(poly.gid, ind)}
                options={{
                  strokeColor:
                    selectedGeometry && selectedGeometry.gid === poly.gid
                      ? "blue"
                      : "red",
                  fillColor: "#8fa225",
                  fillOpacity: 0.3,
                  zIndex: 1000,
                }}
              />
            ))}
            <Marker
              position={{ lat: centerLat, lng: centerLng }}
              clickable={false}
              draggable={false}
            />
            {(selectedPropertyInfo.button || !isSideDrawerVisible) &&
              showDrawingTools && (
                <div className={styles.TopButtonCont}>
                  <TopButton
                    saveGeometries={saveGeometriesHandler}
                    deleteGeometry={deleteGeometryWrapper}
                  />
                </div>
              )}
            <GeometryInfo />
            <WorkOrderInfo />
          </GoogleMap>
        </LoadScript>
      <ConfirmDialog
        isVisible={isDialogVisible}
        continue={propertySelectHelper}
        setDialogVisibility={setDialogVisible}
      />

      <DeleteConfirmDialog
        isVisible={isDeleteDialogVisible}
        delete={deleteGeometry}
        setDeleteDialogVisible={setDeleteDialogVisible}
      />

      <SideDrawer
        setProperty={propertySelectionHandler}
        selectedProperty={selectedPropertyInfo}
        setButtonVisibilityStatus={editButtonHandler}
        isSideDrawerVisible={isSideDrawerVisible}
        toggleSideDrawer={toggleSideDrawer}
      />

      {/* Hiding buttons for going to live and go back while user is creating geofence  */}
      {isSideDrawerVisible && <Greeting switchPath="live" />}
    </div>
  );
};

export default Geo;
